import { Link } from "react-router-dom";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer";
import ResponsiveSection from "@/components/layout/ResponsiveSection";
import ExpertSection from "@/components/ExpertSection";

import { FileText, Check, AlertCircle, TrendingUp, Clock, Shield, Award, Phone, Zap, Package, Scale, Laptop, Stethoscope, Heart, Building2, Activity } from "lucide-react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const Inhaltsversicherung = () => {
  return <div className="min-h-screen bg-background">
      <div className="relative z-10">
        <Navigation />

      {/* Hero Section */}
      <ResponsiveSection spacing="lg">
        <div className="text-center mb-12">
          <Badge className="mb-4 bg-accent text-background">Empfohlen</Badge>
          <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6 break-words">
            Inhaltsversicherung für Arztpraxen
          </h1>
          <p className="text-lg sm:text-xl text-muted-foreground max-w-3xl mx-auto">
            Umfassender Schutz für Ihre Praxiseinrichtung und medizinisches Equipment
          </p>
        </div>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
          <Card className="card-glass text-center">
            <CardContent className="pt-6">
              <Clock className="h-8 w-8 text-accent mx-auto mb-2" />
              <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1">24/7</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Schutz</div>
            </CardContent>
          </Card>
          <Card className="card-glass text-center">
            <CardContent className="pt-6">
              <Award className="h-8 w-8 text-accent mx-auto mb-2" />
              <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1">Neuwert</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Entschädigung</div>
            </CardContent>
          </Card>
          <Card className="card-glass text-center">
            <CardContent className="pt-6">
              <Zap className="h-8 w-8 text-accent mx-auto mb-2" />
              <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1">Schnell</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Regulierung</div>
            </CardContent>
          </Card>
          <Card className="card-glass text-center">
            <CardContent className="pt-6">
              <Shield className="h-8 w-8 text-accent mx-auto mb-2" />
              <div className="text-lg sm:text-xl md:text-2xl font-bold mb-1">Weltweit</div>
              <div className="text-xs sm:text-sm text-muted-foreground">Schutz</div>
            </CardContent>
          </Card>
        </div>
      </ResponsiveSection>

      {/* Leistungsübersicht */}
      <ResponsiveSection spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-3 text-center text-foreground break-words">
          Leistungsübersicht
        </h2>
        <p className="text-sm text-muted-foreground mb-12 text-center">
          Umfassender Schutz für Ihre gesamte Praxisausstattung
        </p>

        <div className="glas-cards grid grid-cols-1 md:grid-cols-2 gap-8 lg:gap-12 xl:gap-16">
          {/* Versicherte Gefahren */}
          <div className="relative pb-6 md:pb-0">
            <div className="relative z-10">
              <div className="flex flex-col lg:flex-row items-center gap-3 mb-3 lg:mb-2">
                <Shield className="w-8 h-8 lg:w-5 lg:h-5 text-accent" />
                <h3 className="text-lg sm:text-xl font-bold text-foreground text-center lg:text-left">
                  Versicherte Gefahren
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4 lg:mb-6 text-center lg:text-left">
                Umfassender Schutz vor den wichtigsten Risiken
              </p>
              
              <div className="list-container">
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Feuer, Blitzschlag, Explosion</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Einbruchdiebstahl und Vandalismus</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Leitungswasser und Rohrbruch</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Sturm und Hagel</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Überspannung durch Blitz</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Elementarschäden (optional)</span>
                </div>
              </div>
            </div>
          </div>

          {/* Versicherte Gegenstände */}
          <div className="relative">
            <div className="relative z-10">
              <div className="flex flex-col lg:flex-row items-center gap-3 mb-3 lg:mb-2">
                <FileText className="w-8 h-8 lg:w-5 lg:h-5 text-accent" />
                <h3 className="text-lg sm:text-xl font-bold text-foreground text-center lg:text-left">
                  Versicherte Gegenstände
                </h3>
              </div>
              <p className="text-sm text-muted-foreground mb-4 lg:mb-6 text-center lg:text-left">
                Schutz für Ihre gesamte Praxisausstattung
              </p>
              
              <div className="list-container">
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Medizinische Geräte und Instrumente</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Praxismöbel und Einrichtung</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">IT-Hardware und Software</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Verbrauchsmaterialien und Arzneimittel</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Patientenunterlagen und Akten</span>
                </div>
                <div className="list-item">
                  <span className="checkmark">
                    <Check className="w-5 h-5 text-accent" />
                  </span>
                  <span className="list-text">Kunst- und Dekorationsgegenstände</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </ResponsiveSection>

      {/* Besondere Vorteile */}
      <ResponsiveSection spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-8 text-center text-foreground break-words">
          Besondere Vorteile
        </h2>

        <div className="glas-cards">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Neuwertentschädigung</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Ersatz zum Neupreis ohne Abzug für Alter oder Abnutzung</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Grobe Fahrlässigkeit mitversichert</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Auch bei Unachtsamkeit bleiben Sie geschützt</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Bewegliche Sachen außerhalb</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Auch bei Hausbesuchen sind Ihre Geräte versichert</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Glasbruch inklusive</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Fenster, Türen, Vitrinen sind automatisch abgesichert</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Aufräumungs- und Entsorgungskosten</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Folgekosten nach einem Schaden sind mitversichert</p>
              </div>
            </div>
            <div className="flex items-start gap-3">
              <Check className="w-6 h-6 text-accent flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Wiederherstellung von Daten</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Elektronische Aufzeichnungen sind geschützt</p>
              </div>
            </div>
          </div>
        </div>
      </ResponsiveSection>

      {/* Für wen geeignet */}
      <ResponsiveSection spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-8 text-center text-foreground break-words">
          Für wen ist die Inhaltsversicherung geeignet?
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <Card className="card-glass text-center">
            <CardHeader>
              <Stethoscope className="w-10 h-10 sm:w-12 sm:h-12 text-accent mb-2 mx-auto" />
              <CardTitle className="text-lg sm:text-xl">Hausarztpraxen</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground/70 text-sm sm:text-base">
                Schutz für Basisausstattung und allgemeinmedizinische Geräte wie EKG, Labor und Ultraschall.
              </p>
            </CardContent>
          </Card>

          <Card className="card-glass text-center">
            <CardHeader>
              <Heart className="w-10 h-10 sm:w-12 sm:h-12 text-accent mb-2 mx-auto" />
              <CardTitle className="text-lg sm:text-xl">Fachärzte</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground/70 text-sm sm:text-base">
                Absicherung hochspezialisierter und teurer Medizintechnik wie Röntgengeräte, OP-Ausstattung oder Lasersysteme.
              </p>
            </CardContent>
          </Card>

          <Card className="card-glass text-center">
            <CardHeader>
              <Building2 className="w-10 h-10 sm:w-12 sm:h-12 text-accent mb-2 mx-auto" />
              <CardTitle className="text-lg sm:text-xl">MVZ & Kliniken</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground/70 text-sm sm:text-base">
                Umfangreiche Deckung für großflächige Einrichtungen mit mehreren Behandlungsräumen und Spezialgeräten.
              </p>
            </CardContent>
          </Card>

          <Card className="card-glass text-center hidden sm:block lg:hidden">
            <CardHeader>
              <Activity className="w-10 h-10 sm:w-12 sm:h-12 text-accent mb-2 mx-auto" />
              <CardTitle className="text-lg sm:text-xl">Therapeutische Praxen</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-foreground/70 text-sm sm:text-base">
                Spezieller Schutz für therapeutische Geräte wie Trainingsgeräte, Behandlungsliegen und Diagnostik-Equipment.
              </p>
            </CardContent>
          </Card>
        </div>
      </ResponsiveSection>

      {/* Schadensbeispiele */}
      <ResponsiveSection spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-8 text-center text-foreground break-words">
          Schadensbeispiele aus der Praxis
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div className="glas-cards text-center">
            <AlertCircle className="w-8 h-8 text-accent mb-4 mx-auto" />
            <h3 className="text-base sm:text-lg font-semibold mb-3 break-words">
              Wasserschaden durch Rohrbruch
            </h3>
            <p className="text-foreground/70 mb-4 text-sm sm:text-base">
              Leitungswasserschaden beschädigt Praxismöbel, Elektronik und medizinische Geräte im Wert von 45.000€.
            </p>
            <div className="pt-4 border-t border-border">
              <p className="text-xs sm:text-sm">
                <strong className="text-accent">Versicherungsleistung:</strong> Vollständig abgedeckt inkl. Neuwerterstattung
              </p>
            </div>
          </div>

          <div className="glas-cards text-center">
            <AlertCircle className="w-8 h-8 text-accent mb-4 mx-auto" />
            <h3 className="text-base sm:text-lg font-semibold mb-3 break-words">
              Einbruch mit Gerätediebstahl
            </h3>
            <p className="text-foreground/70 mb-4 text-sm sm:text-base">
              Einbrecher stehlen medizinische Geräte, IT-Equipment und Medikamente im Gesamtwert von 78.000€.
            </p>
            <div className="pt-4 border-t border-border">
              <p className="text-xs sm:text-sm">
                <strong className="text-accent">Versicherungsleistung:</strong> Neuwerterstattung aller gestohlenen Gegenstände
              </p>
            </div>
          </div>

          <div className="glas-cards text-center">
            <AlertCircle className="w-8 h-8 text-accent mb-4 mx-auto" />
            <h3 className="text-base sm:text-lg font-semibold mb-3 break-words">
              Blitzschlag Überspannung IT
            </h3>
            <p className="text-foreground/70 mb-4 text-sm sm:text-base">
              Überspannungsschaden durch Blitzeinschlag zerstört gesamte IT-Infrastruktur im Wert von 12.000€.
            </p>
            <div className="pt-4 border-t border-border">
              <p className="text-xs sm:text-sm">
                <strong className="text-accent">Versicherungsleistung:</strong> Inkl. Datenwiederherstellung vollständig erstattet
              </p>
            </div>
          </div>

          <div className="glas-cards text-center hidden sm:block lg:hidden">
            <AlertCircle className="w-8 h-8 text-accent mb-4 mx-auto" />
            <h3 className="text-base sm:text-lg font-semibold mb-3 break-words">
              Feuerschaden in Laborbereich
            </h3>
            <p className="text-foreground/70 mb-4 text-sm sm:text-base">
              Brand zerstört Laborausstattung, Probenmaterial und angrenzende Behandlungsräume im Wert von 92.000€.
            </p>
            <div className="pt-4 border-t border-border">
              <p className="text-xs sm:text-sm">
                <strong className="text-accent">Versicherungsleistung:</strong> Neuwert inkl. Aufräumung und Entsorgungskosten vollständig erstattet
              </p>
            </div>
          </div>
        </div>
      </ResponsiveSection>

      {/* Empfohlene Versicherungssummen */}
      <ResponsiveSection spacing="lg" containerSize="md">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-8 text-center text-foreground break-words">
          Empfohlene Versicherungssummen
        </h2>

        <Card className="card-glass-highlight">
          <CardContent className="pt-6">
            <div className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 card-glass-subtle rounded-lg">
                <div>
                  <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Kleine Praxis (1-2 Ärzte)</h3>
                  <p className="text-xs sm:text-sm text-foreground/70">Hausarzt, kleiner Facharzt</p>
                </div>
                <div className="text-left sm:text-right">
                  <div className="text-sm sm:text-base md:text-lg font-bold text-accent whitespace-nowrap">100.000 - 250.000€</div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 card-glass-subtle rounded-lg">
                <div>
                  <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Mittlere Praxis (3-5 Ärzte)</h3>
                  <p className="text-xs sm:text-sm text-foreground/70">Gemeinschaftspraxis, größerer Facharzt</p>
                </div>
                <div className="text-left sm:text-right">
                  <div className="text-sm sm:text-base md:text-lg font-bold text-accent whitespace-nowrap">250.000 - 500.000€</div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 p-4 card-glass-subtle rounded-lg">
                <div>
                  <h3 className="font-bold text-foreground mb-1 text-sm sm:text-base">Große Praxis/MVZ</h3>
                  <p className="text-xs sm:text-sm text-foreground/70">MVZ, Klinik, spezialisierte Einrichtung</p>
                </div>
                <div className="text-left sm:text-right">
                  <div className="text-sm sm:text-base md:text-lg font-bold text-accent whitespace-nowrap">500.000 - 1.500.000€</div>
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-yellow-600/10 border border-yellow-600/30 rounded-lg">
              <p className="text-xs sm:text-sm text-foreground/70">
                <strong className="text-foreground">Wichtig:</strong> Die Versicherungssumme sollte dem tatsächlichen Neuwert 
                aller Gegenstände entsprechen, um Unterversicherung zu vermeiden.
              </p>
            </div>
          </CardContent>
        </Card>
      </ResponsiveSection>

      {/* Weitere Versicherungen */}
      <ResponsiveSection variant="card" spacing="lg">
        <h2 className="text-xl sm:text-2xl md:text-3xl font-bold mb-8 text-center text-foreground break-words">
          Weitere wichtige Versicherungen
        </h2>

        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <Link to="/weitere-versicherungen/berufshaftpflicht" className="block">
            <Card className="h-full card-glass">
              <CardContent className="pt-6">
                <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">Berufshaftpflicht</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Schutz bei Behandlungsfehlern</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/tarifwissen" className="block">
            <Card className="h-full card-glass">
              <CardContent className="pt-6">
                <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">Cyberversicherung</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Schutz vor digitalen Bedrohungen</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/weitere-versicherungen/rechtsschutzversicherung" className="block">
            <Card className="h-full card-glass">
              <CardContent className="pt-6">
                <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">Rechtsschutz</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Rechtliche Unterstützung</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/weitere-versicherungen/praxisausfallversicherung" className="block">
            <Card className="h-full card-glass">
              <CardContent className="pt-6">
                <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">Praxisausfall</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Bei Betriebsunterbrechung</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/weitere-versicherungen/elektronikversicherung" className="block">
            <Card className="h-full card-glass">
              <CardContent className="pt-6">
                <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">Elektronikversicherung</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Für medizinische IT</p>
              </CardContent>
            </Card>
          </Link>

          <Link to="/weitere-versicherungen" className="block">
            <Card className="h-full card-glass">
              <CardContent className="pt-6">
                <h3 className="font-bold text-foreground mb-2 text-sm sm:text-base">Alle Versicherungen</h3>
                <p className="text-xs sm:text-sm text-foreground/70">Komplette Übersicht</p>
              </CardContent>
            </Card>
          </Link>
        </div>
      </ResponsiveSection>

      {/* CTA */}
      <section className="py-12 sm:py-16 px-4 sm:px-6 lg:px-8">
        <div className="container mx-auto max-w-4xl text-center">
          <Phone className="w-12 h-12 text-accent mx-auto mb-4" />
          <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-foreground">
            Persönliche Beratung zur Inhaltsversicherung
          </h2>
          <p className="text-foreground/70 mb-8 text-base sm:text-lg max-w-2xl mx-auto">
            Lassen Sie sich kostenlos beraten, welche Versicherungssumme für Ihre Praxis optimal ist 
            und welche Zusatzbausteine sinnvoll sind.
          </p>
          <Button size="lg" asChild className="bg-accent hover:bg-accent/90 text-background">
            <Link to="/termin-buchen">
              Jetzt Beratungstermin vereinbaren
            </Link>
          </Button>
        </div>
      </section>

      <ExpertSection />
      <Footer />
      </div>
    </div>;
};
export default Inhaltsversicherung;